import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import {
  Box,
  ButtonBase,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCopyToClipboard } from 'react-use';

import { Flex } from '@/components/utils';
import { useTranslate } from '@/hooks';

interface PopoverItemProps {
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  onClick?: () => void;
  to: string;
  event?: () => void;
  isLogout?: boolean;
}

export const PopoverItem = (props: PropsWithChildren<PopoverItemProps>) => {
  const { palette } = useTheme();
  const { startAdornment, endAdornment, to, event, isLogout } = props;

  return (
    <ButtonBase
      sx={{
        justifyContent: 'flex-start',
        width: '100%',
        borderRadius: '5px',
        '&:hover': {
          backgroundColor: palette.action.hover,
        },
        '& a': {
          p: '0.5rem',
          color: palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none',
          gap: '5px',
          width: '100%',
          justifyContent: 'flex-start',
          '& .PopoverItem-text': {
            flex: 1,
            fontSize: '12px',
            textAlign: 'start',
            ...(isLogout && {
              color: palette.error.main,
            }),
          },
          '& .MuiSvgIcon-root': {
            height: '20px',
            width: '20px',
            ...(isLogout && {
              color: palette.error.main,
            }),
          },
        },
      }}
    >
      <Link onClick={event} to={to}>
        {startAdornment}
        <span className="PopoverItem-text">{props.children}</span>
        {endAdornment}
      </Link>
    </ButtonBase>
  );
};

interface CopyIDButtonProps {
  companyId: string;
}

export const CopyIDButton = ({ companyId }: CopyIDButtonProps) => {
  const { palette } = useTheme();
  const [copied, setCopied] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, copyToClipboard] = useCopyToClipboard();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslate('profileButtom');

  const handleCopy = () => {
    copyToClipboard(companyId);
    setCopied(true);
    timeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Flex gap="5px" column p="5px">
      <Typography
        sx={{
          fontSize: '12px',
        }}
      >
        {t('accountID')}
      </Typography>
      <Box
        sx={{
          color: palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          '& .PopoverItem-text': {
            flex: 1,
            fontSize: '12px',
          },
          '& .MuiSvgIcon-root': {
            height: '20px',
            width: '20px',
          },
        }}
      >
        <span className="PopoverItem-text">{companyId}</span>
        <IconButton
          onClick={handleCopy}
          sx={{
            transition: 'all 0.2s ease-in-out',
            '& .MuiSvgIcon-root': {
              color: copied ? 'white' : 'inherit',
            },
            backgroundColor: copied ? palette.success.light : 'inherit',
            '&:hover': {
              backgroundColor: copied ? palette.success.light : 'inherit',
              '& .MuiSvgIcon-root': {
                color: copied ? 'white' : 'inherit',
              },
            },
          }}
        >
          {copied ? <CheckCircleOutline /> : <ContentCopyOutlined />}
        </IconButton>
      </Box>
    </Flex>
  );
};
